var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
/**
 * @module cercaliagl/control/NavBar
 */
import Control from './Control';
import MapControls from './MapControls';
import Proj from '../proj';
import { currentDictionaryLanguage, i18n } from '../dictionary';
import { IMG, IS_MOBILE } from '../globals';
import LonLat from '../LonLat';
import Marker from '../Marker';
import Feature from '../Feature';
import Icon from '../Icon';
import Popup from '../Popup';
import SimpleLabel from '../SimpleLabel';
import Notification, { Severity } from '../Notification';
import PoisMap from '../MapPois';
import Autocomplete, { FilterTypes as AutocompleteFilterTypes } from '../service/Autocomplete';
import Proximity from '../service/Proximity';
import ServicePois from '../service/Pois';
import { wktToGeometry, downloadKmlFromWkt, createCircleGeometry, isMobile, } from '../util';
import { getCategories, getCountries } from '../data';
import { MarkerType as MapRoutingMarkerType, EventType as MapRoutingEventType } from '../MapRouting';
import VNavBar, { Buttons as _Buttons, EventType as NavBarEventType, View } from '../view/VNavBar';
import { EventType as BaseViewEventType } from '../view/BaseView';
import { EventType as WidgetSearchEventType } from '../view/VWidgetSearch';
import { EventType as WidgetRoutingEventType } from '../view/VWidgetRouting';
import { EventType as WidgetPoisEventType, SearchType as PoisSearchType } from '../view/VWidgetPois';
import MarkerLayer from '../layer/Marker';
import FeatureLayer from '../layer/Feature';
/**
 * @typedef {Object} RoutingAutocompletes
 * @property {Autocomplete} origin
 * @property {Autocomplete} destination
 * @property {Array.<Autocomplete>} steps
 */
/**
 * @enum {string}
 */
var RoutingReportSigns = {
    ROUNDABOUT: 'roundabout.png',
    FERRY: 'ferry.png',
    FUNICULAR: 'funicular.png',
    KEEP_RIGHT: 'keep-right.png',
    KEEP_LEFT: 'keep-left.png',
    TURN_RIGHT: 'turn-right.png',
    TURN_LEFT: 'turn-left.png',
    RECTUM: 'rectum.png',
};
/**
 * @enum {string}
 */
var LayerId = {
    MARKER_LAYER: 'NavBarMarkerLayer',
    FEATURE_LAYER: 'NavBarFeatureLayer',
};
/**
 * `cercaliagl.control.NavBar.Buttons.ADDRESS`<br/>
 * `cercaliagl.control.NavBar.Buttons.POIS`<br/>
 * `cercaliagl.control.NavBar.Buttons.ROUTING`<br/>
 * @type {Object.<string, string>}
 * @api
 */
export var Buttons = _Buttons;
/**
 * @classdesc
 * <b>Javascript ES5</b>
 * ```js
 * const control = new cercaliagl.control.NavBar();
 * ```
 * <br/>
 * Class for nav bar control which offers to search addresses, calculate routes and search points.
 * This class can be obtained via map object.
 *
 * All autocomplete components element gets addresses by country priority depends where map is centered.
 * It is possible to access to some functionalities by published methods listed in this page.
 *
 *
 * To access to `NavBar` control element you can do it:
 *
 * ```
 * const map = new cercaliagl.Map({ target: 'map', controls: [cercaliagl.control.MapControls.NAVBAR] });
 * const navbarControl = map.getControlByName(cercaliagl.control.MapControls.NAVBAR);
 * ```
 *
 * @api
 */
var NavBar = /** @class */ (function (_super) {
    __extends(NavBar, _super);
    function NavBar() {
        var _this = this;
        var navBar = new VNavBar();
        var element = navBar.getElement();
        _this = _super.call(this, {
            element: element,
            className: 'cercaliagl.control.NavBar',
            name: MapControls.NAVBAR,
        }) || this;
        /**
         * @private
         * @type {Element}
         */
        _this.element_ = element;
        /**
         * @private
         * @type {VNavBar}
         */
        _this.vNavBar_ = navBar;
        /**
         * @private
         * @type {import("../view/VWidgetSearch.js").default}
         */
        _this.searchWidget_ = navBar.getWidgetSearch();
        /**
         * @private
         * @type {import("../view/VWidgetRouting.js").default}
         */
        _this.routingWidget_ = navBar.getWidgetRouting();
        /**
         * @private
         * @type {import("../view/VWidgetPois.js").default}
         */
        _this.poisWidget_ = navBar.getWidgetPois();
        /**
         * @private
         * @type {import('../Map').default}
         */
        _this.cercaliaMap_ = null;
        /**
         * @private
         * @type {import('../MapRouting').default}
         */
        _this.mapRouting_ = null;
        /**
         * @private
         * @type {Autocomplete}
         */
        _this.navBarAutocomplete_ = null;
        /**
         * @private
         * @type {import('../Feature').default}
         */
        _this.substageFeature_ = null;
        /**
         * @private
         * @type {import('../Marker').default}
         */
        _this.substageMarker_ = null;
        /**
         * @private
         * @type {boolean}
         */
        _this.substageClicked_ = false;
        /**
         * @private
         * @type {import('../MapPois').default}
         */
        _this.mapPois_ = null;
        /**
         * @private
         * @type {import('../layer/Marker').default}
         */
        _this.markerLayer_ = null;
        /**
         * @private
         * @type {import('../layer/Feature').default}
         */
        _this.featureLayer_ = null;
        /**
         * @private
         * @type {import('../service/Proximity').default}
         */
        _this.proximityService_ = new Proximity();
        /**
         * @private
         * @type {Array.<Marker>}
         */
        _this.markersProximity_ = [];
        /**
         * @private
         * @type {Feature}
         */
        _this.circleProximity_ = null;
        /**
         * @private
         * @type {Marker}
         */
        _this.markerProximityCenter_ = null;
        /**
         * @private
         * @type {Array.<import('../service/Autocomplete').default>}
         */
        _this.listAutocompletes_ = [];
        /**
         * @private
         * @type {RoutingAutocompletes}
         */
        _this.routingAutocompletes_ = null;
        /**
         * @private
         * @type {LonLat}
         */
        _this.lastPositionProxReq_ = new LonLat(0, 0);
        /**
         * @private
         * @type {number}
         */
        _this.lastZoom_ = Infinity;
        /**
         * @private
         * @type {import('../Map').default|null}
         */
        _this.mapPrint_ = null;
        /**
         * @private
         * @type {Array.<string>}
         */
        _this.poisCategoriesIdCustomPopup_ = ['D00GNC'];
        /**
         * @private
         * @type {Function|null}
         */
        _this.customAutocompleteAddressCallback_ = null;
        /**
         * @private
         * @type {Function|null}
         */
        _this.customCoordinatesSearchCallback_ = null;
        /**
         * @private
         * @type {string}
         */
        _this.GAS_STATION_POI_ID_ = 'D00IDEESS2540D';
        /**
       * @constant
       * @private
       * @type {number}
       */
        _this.AUTOCOMPLETE_RADIUS_ = 6000;
        /**
       * @constant
       * @private
       * @type {number}
       */
        _this.AUTOCOMPLETE_SEARCH_MINZOOM_ = 4;
        /**
         * @private
         * @type {Object|null}
         */
        _this.gasolinePrices_ = null;
        /**
         * @private
         * @type {string}
         */
        _this.preferredCountry_ = null;
        _this.element_.setAttribute('id', _this.id_);
        return _this;
    }
    /**
     * Initialize class
     */
    NavBar.prototype.initialize = function () {
        this.vNavBar_.emit(BaseViewEventType.ATTACHED, this.element_.parentElement);
        this.cercaliaMap_ = this.getCercaliaMap();
        this.mapRouting_ = this.cercaliaMap_.getMapRouting();
        this.mapRouting_.setRoutingWidget(this.routingWidget_);
        this.handleNavBarRoutingSingleClick_ = this.handleNavBarRoutingSingleClick_.bind(this);
        this.initializeNavBarAutocomplete_();
        this.initializeSearchWidget_();
        this.initializeRoutingWidget_();
        this.initializePoisWidget_();
        this.initializeMapMenuEvents_();
    };
    /**
     * Initialize layers. Order matters!
     * @private
     */
    NavBar.prototype.initializeLayers_ = function () {
        this.featureLayer_ = new FeatureLayer({
            id: LayerId.FEATURE_LAYER,
            map: this.cercaliaMap_,
        });
        this.markerLayer_ = new MarkerLayer({
            id: LayerId.MARKER_LAYER,
            map: this.cercaliaMap_,
        });
        this.cercaliaMap_.addLayer(this.featureLayer_);
        this.cercaliaMap_.addLayer(this.markerLayer_);
    };
    /**
     * Show button form UI element. Buttons available to show/hide are for widgets: search address, calculate route, search POIs.
     * You can access this values via constants. Show `buttonName` parameter description.
     * @param {string} buttonName Button to hide on NavBar control. Possible values: <br/>
     *   - `cercaliagl.control.NavBar.Buttons.ADDRESS`<br/>
     *   - `cercaliagl.control.NavBar.Buttons.POIS`<br/>
     *   - `cercaliagl.control.NavBar.Buttons.ROUTING`<br/>
     * @api
     */
    NavBar.prototype.hideButton = function (buttonName) {
        this.vNavBar_.hideButton(buttonName);
    };
    /**
     * Show button form UI element. Buttons available to show/hide are for widgets: search address, calculate route, search POIs.
     * You can access this values via constants. Show `buttonName` parameter description.
     * @param {string} buttonName Button to show on NavBar control. Possible values: <br/>
     *   - `cercaliagl.control.NavBar.Buttons.ADDRESS`<br/>
     *   - `cercaliagl.control.NavBar.Buttons.POIS`<br/>
     *   - `cercaliagl.control.NavBar.Buttons.ROUTING`<br/>
     * @api
     */
    NavBar.prototype.showButton = function (buttonName) {
        this.vNavBar_.showButton(buttonName);
    };
    /**
     * @return {VNavBar} NavBar
     */
    NavBar.prototype.getNavBarView = function () {
        return this.vNavBar_;
    };
    /**
     * @private
     */
    NavBar.prototype.initializeMapMenuEvents_ = function () {
        var mbMap = this.getMap();
        mbMap.on('moveend', this.handleMoveEnd_.bind(this));
    };
    /**
     * @private
     */
    NavBar.prototype.handleMoveEnd_ = function () {
        this.autocompleteGeoSearchUpdate_();
    };
    /**
     * @private
     * @param {string} entities Entities.
     */
    NavBar.prototype.autocompleteGeoSearchUpdate_ = function () {
        var zoom = this.cercaliaMap_.getZoom();
        var radius = zoom > this.AUTOCOMPLETE_SEARCH_MINZOOM_ ? 6000 : null;
        var center = zoom > this.AUTOCOMPLETE_SEARCH_MINZOOM_ ? this.cercaliaMap_.getCenter() : null;
        for (var i = 0; i < this.listAutocompletes_.length; i += 1) {
            var autocomplete = this.listAutocompletes_[i];
            autocomplete.setSearchCoordinateReference(center);
            autocomplete.setSearchInsideRadius(radius);
        }
    };
    /**
     * @private
     */
    NavBar.prototype.initializeNavBarAutocomplete_ = function () {
        var _this = this;
        var autocompleteId = this.vNavBar_.getAutocompleteId();
        // autocompleteElement.setAttribute('id', id);
        // autocompleteElement.setAttribute('label', i18n(''Search']);
        this.navBarAutocomplete_ = new Autocomplete(/** @type {import('../service/Autocomplete').AutocompleteOptions} */ ({
            autocompleteId: autocompleteId,
            callbackFunction: this.handleAutocompleteAddressSelect_.bind(this),
        }));
        this.navBarAutocomplete_.setSearchFilterType([
            AutocompleteFilterTypes.CITIES,
            AutocompleteFilterTypes.STREETS,
            AutocompleteFilterTypes.POIS,
            AutocompleteFilterTypes.POSTAL_CODES,
            AutocompleteFilterTypes.POSTAL_CODES_SHORT,
        ]);
        this.listAutocompletes_.push(this.navBarAutocomplete_);
        this.vNavBar_.adjustByDevice();
        document.addEventListener('resize', function () {
            _this.vNavBar_.adjustByDevice();
            _this.searchWidget_.adjustByDevice();
            _this.routingWidget_.adjustByDevice();
            _this.poisWidget_.adjustByDevice();
        });
    };
    /**
     * Get autocomplete search address service object. More details: {@link module:cercaliagl/service/Autocomplete~Autocomplete}.
     * @api
     * @return {Autocomplete} Autocomplete service.
     */
    NavBar.prototype.getNavBarAutocompleteService = function () {
        return this.navBarAutocomplete_;
    };
    /**
     * @private
     * @param {import('../servers').GeEntity|LonLat} result GeEntity or position
     */
    NavBar.prototype.handleAutocompleteAddressSelect_ = function (result) {
        var addressControl = /** @type {import('./Address').default} */ (this.viewInterface_.getControlByName(MapControls.ADDRESS));
        if (result instanceof LonLat) {
            var position = result;
            addressControl.getDirection(position, false, false, false, true);
            this.cercaliaMap_.setCenter(position, 12);
        }
        else if (result) {
            var ge = result;
            if (!this.customAutocompleteAddressCallback_) {
                addressControl.goToGeEntity(ge, true);
            }
            else {
                this.customAutocompleteAddressCallback_.call(this, ge);
            }
        }
    };
    /**
     * Set custom callback function for address search. Set `null` to disable custom function and use default.
     * This is useful if user wants to override default behaviour.
     * Unique parameter received is {@link import('../servers').GeEntity GeEntity}.
     *
     * Example usage:
     * ```
     * navBarControl.setAutocompleteAddressCallback(function(data) {
     *   const country = data.country.value;
     *   const municipality = data.municipality.value;
     *   const coordX = parseFloat(data.coord.x);
     *   const coordY = parseFloat(data.coord.y);
     *   console.log(country, municipality, coordY + ',' + coordX);
     * });
     * ```
     *
     * @param {function(import('../servers').GeEntity)|null} fn Callback function. `null` to use default API behaviour.
     * @api
     */
    NavBar.prototype.setAutocompleteAddressCallback = function (fn) {
        this.customAutocompleteAddressCallback_ = fn;
    };
    /**
     * Set custom callback function for coordinates search, same behaviour as customAutocompleteAdressCallback. Set `null` to disable custom function and use default.
     * @param {function(LonLat)|null} fn Callback function. `null` to use default API behaviour.
     * @api
     */
    NavBar.prototype.setCoordinatesSearchCallback = function (fn) {
        this.customCoordinatesSearchCallback_ = fn;
    };
    /**
     * @private
     */
    NavBar.prototype.initializeSearchWidget_ = function () {
        this.initializeSearchWidgetAutocomplete_();
        this.initializeSearchWidgetSelectCountries_();
        this.initializeSearchWidgetCoordinates_();
    };
    /**
     * @private
     */
    NavBar.prototype.initializeSearchWidgetAutocomplete_ = function () {
        var autocompleteService = new Autocomplete({
            autocompleteId: this.searchWidget_.getAutocompleteId(),
            callbackFunction: this.handleAutocompleteAddressSelect_.bind(this),
            floatingLabel: true,
        });
        this.listAutocompletes_.push(autocompleteService);
        this.searchWidget_.on(WidgetSearchEventType.OPTIONS_CHANGED, function (data) {
            NavBar.handleSearchWidgetOptionsChanged_(autocompleteService, data);
        });
    };
    /**
     * @private
     * @param {string} type Type step. Origin, destination or step
     * @param {number} [numStep] Number step
     * @return {Autocomplete} Autocomplete service
     */
    NavBar.prototype.getAutocompleteRouting_ = function (type, numStep) {
        if (type === MapRoutingMarkerType.ORIGIN) {
            return this.routingAutocompletes_.origin;
        }
        if (type === MapRoutingMarkerType.DESTINATION) {
            return this.routingAutocompletes_.destination;
        }
        return this.routingAutocompletes_.steps[numStep];
    };
    /**
     * @private
     * @param {Autocomplete} autocompleteService Cercalia autocomplete service.
     * @param {import("../view/VWidgetSearch").AddressSearchOption} data Data
     */
    NavBar.handleSearchWidgetOptionsChanged_ = function (autocompleteService, data) {
        autocompleteService.setSearchFilterType(data.getype);
        if (data.ctryc) {
            autocompleteService.setSearchInsideCountry(data.ctryc);
        }
        else {
            autocompleteService.setSearchInsideCountry(null);
        }
    };
    /**
     * @private
     */
    NavBar.prototype.initializeSearchWidgetSelectCountries_ = function () {
        var _this = this;
        getCountries(function (data) {
            if (data) {
                _this.searchWidget_.setCountries(data);
            }
        });
    };
    /**
     * @private
     */
    NavBar.prototype.initializeSearchWidgetCoordinates_ = function () {
        var _this = this;
        this.searchWidget_.on(WidgetSearchEventType.COORD_SUBMIT, function (data) {
            var coordOptions = /** @type {import("../view/VWidgetSearch").CoordinateSearchOption} */ (data);
            var addressControl = /** @type {import('./Address').default} */ (_this.viewInterface_.getControlByName(MapControls.ADDRESS));
            var position = new LonLat(coordOptions.x, coordOptions.y, coordOptions.srs);
            if (!_this.customCoordinatesSearchCallback_) {
                if (coordOptions.nearest) {
                    addressControl.getDirection(position, function () {
                        _this.cercaliaMap_.setCenter(position, 14, true);
                    });
                }
                else {
                    addressControl.goToCoordinates(position, true);
                }
            }
            else if (coordOptions.nearest) {
                addressControl.getDirection(position, function (lonlat, ge) {
                    _this.customCoordinatesSearchCallback_.call(_this, { position: lonlat, ge: ge });
                }, true, true);
            }
            else {
                _this.customCoordinatesSearchCallback_.call(_this, { position: position, ge: null });
            }
        });
    };
    /**
     * @private
     */
    NavBar.prototype.initializeRoutingWidget_ = function () {
        this.routingAutocomplete_();
        this.routingEvents_();
    };
    /**
     * @private
     */
    NavBar.prototype.routingEvents_ = function () {
        var _this = this;
        this.vNavBar_.on(NavBarEventType.ROUTING, function () { return _this.handleNavBarRoutingEvent_(); });
        this.routingWidget_.on(WidgetRoutingEventType.STEPS_CHANGED, function (routingStep) { return _this.handleStepsChanged_(routingStep); });
        this.routingWidget_.on(WidgetRoutingEventType.STEP_REMOVED, function (stepIndex) { return _this.handleStepRemoved_(stepIndex); });
        this.routingWidget_.on(WidgetRoutingEventType.ROUTE_REMOVED, function () { return _this.handleRouteRemoved_(); });
        this.routingWidget_.on(WidgetRoutingEventType.OPTIONS_CHANGED, function (routingOptions) { return _this.handleRouteOptionsChanged_(routingOptions); });
        this.routingWidget_.on(WidgetRoutingEventType.GET_REPORT, function (routeIndex) { return _this.handleRouteGetReport_(routeIndex); });
        this.routingWidget_.on(WidgetRoutingEventType.SELECT_SUMMARY, function (routeIndex) { return _this.handleSelectSummary_(routeIndex); });
        this.routingWidget_.on(WidgetRoutingEventType.CLOSE_ROUTING, function () { return _this.handleCloseRoutingMenu_(); });
        this.routingWidget_.on(WidgetRoutingEventType.INVERT_ROUTE, function () { return _this.handleInvertRoute_(); });
        this.routingWidget_.on(WidgetRoutingEventType.CLOSE_REPORT, function () { return _this.handleCloseReport_(); });
        this.routingWidget_.on(WidgetRoutingEventType.SAVE_ROUTE, function () { return _this.handleSaveRoute_(); });
        // this.routingWidget_.on(WidgetRoutingEventType.PRINT_ROUTE, () => this.handlePrintRoute_());
        this.routingWidget_.on(WidgetRoutingEventType.REORDER_STEPS, function (stepsDragged) { return _this.handleReorderSteps_(stepsDragged); });
        // Report events
        this.routingWidget_.on(WidgetRoutingEventType.TAP_SUBSTAGE, function (substageData) { return _this.handleMouseTapSubstage_(substageData); });
        this.routingWidget_.on(WidgetRoutingEventType.MOUSEENTER_SUBSTAGE, function (substageData) { return _this.handleMouseEnterSubstage_(substageData); });
        this.routingWidget_.on(WidgetRoutingEventType.MOUSELEAVE_SUBSTAGE, function (substageData) { return _this.handleMouseLeaveSubstage_(substageData); });
        // MapRouting events.
        this.mapRouting_.once(MapRoutingEventType.MAP_ADDED, function () { return _this.initializeLayers_(); });
        this.mapRouting_.on(MapRoutingEventType.ROUTE_CALC_STARTED, function (e) { return _this.handleMapRouteStarted_(e); });
        this.mapRouting_.on(MapRoutingEventType.ROUTE_CHANGED, function (e) { return _this.handleMapRoutingChanged_(e); });
        this.mapRouting_.on(MapRoutingEventType.ROUTE_REMOVED, function () { return _this.handleRouteRemoved_(); });
        this.mapRouting_.on(MapRoutingEventType.ROUTE_CALCULATED, function (e) { return _this.handleRouteCalculated_(e); });
        this.mapRouting_.on(MapRoutingEventType.ROUTE_REMOVE_STEP, function (e) { return _this.handleMapRouteRemoveStep_(e); });
        this.mapRouting_.on(MapRoutingEventType.ROUTE_ALT_SELECT, function (e) { return _this.handleRouteAltSelect_(e); });
    };
    /**
     * @private
     */
    NavBar.prototype.handleNavBarRoutingEvent_ = function () {
        this.unlistenSingleclickMapRouting_();
        var mbMap = this.getMap();
        mbMap.on('click', this.handleNavBarRoutingSingleClick_);
        var addressControl = /** @type {import('./Address.js').default} */ (this.viewInterface_.getControlByName(MapControls.ADDRESS));
        var currentGe = addressControl.getCurrentAddress();
        if (currentGe) {
            addressControl.directionTo();
        }
        if (this.gasolinePrices_ === null) {
            this.updateGasolinePrices_();
        }
    };
    /**
     * @private
     * @param {import('maplibre-gl').MapMouseEvent} event Event.
     */
    NavBar.prototype.handleNavBarRoutingSingleClick_ = function (event) {
        var position = LonLat.fromMapboxgl(event.lngLat);
        if (this.mapRouting_.getMarkerOrigin() === null) {
            this.mapRouting_.setRoutingStep(MapRoutingMarkerType.ORIGIN, position);
        }
        else if (this.mapRouting_.getMarkerDestination() === null) {
            this.mapRouting_.setRoutingStep(MapRoutingMarkerType.DESTINATION, position);
        }
        if (!this.mapRouting_.isReady()) {
            this.handleNavBarRoutingEvent_();
        }
    };
    /**
     * @private
     */
    NavBar.prototype.unlistenSingleclickMapRouting_ = function () {
        var mbMap = this.getMap();
        mbMap.off('click', this.handleNavBarRoutingSingleClick_);
    };
    /**
     * @private
     */
    NavBar.prototype.handleCloseRoutingMenu_ = function () {
        this.handleRouteRemoved_();
        this.unlistenSingleclickMapRouting_();
    };
    /**
     * @private
     */
    NavBar.prototype.handleInvertRoute_ = function () {
        this.mapRouting_.invert();
    };
    /**
     * @private
     * @param {import("../view/VWidgetRouting").StepsDraggedData} stepsDragged Steps dragged
     */
    NavBar.prototype.handleReorderSteps_ = function (stepsDragged) {
        this.mapRouting_.reorderSteps(stepsDragged.oldIndex, stepsDragged.newIndex);
    };
    /**
     * @private
     * @param {import("../view/VWidgetRouting").NewStepRouting} stepRouting Step routing
     */
    NavBar.prototype.handleStepsChanged_ = function (stepRouting) {
        var stepElement = this.routingWidget_.getAutocompleteByStepNum(stepRouting.numStep);
        var autocompleteService = new Autocomplete(/** @type {import('../service/Autocomplete').AutocompleteOptions} */ ({
            autocompleteId: stepElement.getAttribute('id'),
            label: i18n('waypoint'),
            searchPreferredEntity: this.preferredCountry_,
            callbackFunction: this.handleAutocompleteRoutingSelect_.bind(this, MapRoutingMarkerType.STEP),
        }));
        autocompleteService.setValue(stepRouting.formattedAddress);
        this.listAutocompletes_.push(autocompleteService);
        this.routingAutocompletes_.steps.push(autocompleteService);
    };
    /**
     * @private
     * @param {number} stepIndex Step index
     */
    NavBar.prototype.handleStepRemoved_ = function (stepIndex) {
        this.mapRouting_.removeStepIndex(stepIndex);
        this.routingAutocompletes_.steps.splice(stepIndex, 1);
    };
    /**
     * @private
     */
    NavBar.prototype.handleRouteRemoved_ = function () {
        this.routingWidget_.setSummary(null);
        this.routingWidget_.setReport(null);
        this.resetRoutingAutocompletes_();
        if (this.mapRouting_.isReady()) {
            this.mapRouting_.cleanRoute();
        }
        else {
            this.mapRouting_.removeMarkers();
        }
        this.routingWidget_.setRouteDrawn(false);
    };
    /**
     * @private
     */
    NavBar.prototype.resetRoutingAutocompletes_ = function () {
        this.routingAutocompletes_.origin.setValue(null);
        this.routingWidget_.removeAllSteps();
        this.routingAutocompletes_.steps = [];
        this.routingAutocompletes_.destination.setValue(null);
    };
    /**
     * @private
     * @param {number} routeIndex Route index
     */
    NavBar.prototype.handleRouteGetReport_ = function (routeIndex) {
        var _this = this;
        this.routingWidget_.setLoading(true);
        this.mapRouting_.calculateReport(function () {
            var routes = _this.mapRouting_.getRoutingService().getRoutes();
            _this.routingWidget_.setReport(routes[routeIndex], _this.routingAutocompletes_);
            _this.routingWidget_.setLoading(false);
        });
        this.mapRouting_.toggleFeaturesAlts(false);
    };
    /**
     * @private
     * @param {number} routeIndex Route index
     */
    NavBar.prototype.handleSelectSummary_ = function (routeIndex) {
        this.mapRouting_.selectAlternativeRoute(routeIndex);
    };
    /**
     * @private
     * @param {import('../MapRouting').RouteCalculatedEvent} event Event.
     */
    NavBar.prototype.handleRouteCalculated_ = function (event) {
        var summaries = event.summaries;
        this.viewInterface_.closePopups();
        this.routingWidget_.setReport(null);
        this.routingWidget_.setSummary(summaries);
        this.routingWidget_.setRouteDrawn(true);
        this.routingWidget_.setLoading(false);
        this.reorderRoutingWidget_();
    };
    /**
     * Only reorders if optimization is enabled.
     * @private
     */
    NavBar.prototype.reorderRoutingWidget_ = function () {
        var routingService = this.mapRouting_.getRoutingService();
        if (routingService.isReorder()) {
            var markerSteps = this.mapRouting_.getMarkerSteps();
            var formattedAddress = void 0;
            var elem = void 0;
            var i = void 0;
            for (i = 0; i < markerSteps.length; i += 1) {
                var marker = markerSteps[i];
                var numStep = marker.get('numStep');
                var indexElem = numStep - 1;
                elem = this.routingAutocompletes_.steps[indexElem];
                formattedAddress = marker.getSimpleLabel().getText();
                formattedAddress = formattedAddress.replace('\n', ' ');
                elem.setValue(formattedAddress);
            }
        }
    };
    /**
     * @private
     * @param {import("../MapRouting").ContextMenuEvent} event Step number to delete.
     */
    NavBar.prototype.handleMapRouteRemoveStep_ = function (event) {
        var numStep = event.numStep;
        this.routingWidget_.removeStepIndex(numStep, false);
        this.routingAutocompletes_.steps.splice(numStep, 1);
    };
    /**
     * @private
     * @param {import("../MapRouting").SelectAltRouteEvent} event Select alternative route event
     */
    NavBar.prototype.handleRouteAltSelect_ = function (event) {
        this.routingWidget_.selectSummary(event.routeIndex);
    };
    /**
     * @private
     * @param {import("../view/VWidgetRouting").RoutingOptions} routingOptions Routing options
     */
    NavBar.prototype.handleRouteOptionsChanged_ = function (routingOptions) {
        this.mapRouting_.updateOptions(routingOptions.weight, routingOptions.reorder, routingOptions.isLogistics, routingOptions.logisticParams, routingOptions.departuretime, routingOptions.routingOptions_);
    };
    /**
     * @private
     */
    NavBar.prototype.handleMapRouteStarted_ = function () {
        this.unlistenSingleclickMapRouting_();
        this.routingWidget_.toggleSettings(false);
        this.routingWidget_.setSummary(null);
        this.routingWidget_.setReport(null);
        this.routingWidget_.setLoading(true);
    };
    /**
     * @private
     * @param {import('../MapRouting').MapRoutingEvent} event Event.
     */
    NavBar.prototype.handleMapRoutingChanged_ = function (event) {
        var marker = event.marker;
        var ge = event.ge;
        var formattedAddress = event.formattedAddress;
        var type = marker.get('type'); // origin, destination, step
        var numStep = marker.get('numStep'); // origin&destination -> undefined
        var autocompleteService = this.getAutocompleteRouting_(type, numStep - 1);
        if (autocompleteService) {
            autocompleteService.setValue(formattedAddress);
        }
        else {
            this.routingWidget_.addStep(ge, formattedAddress);
        }
        if (this.vNavBar_.getCurrentView() !== View.ROUTING) {
            this.vNavBar_.showRoutingMenu();
        }
    };
    /**
     * @private
     */
    NavBar.prototype.routingAutocomplete_ = function () {
        var originAutocomplete = this.routingWidget_.getAutocompleteOrigin();
        var destinationAutocomplete = this.routingWidget_.getAutocompleteDestination();
        var autocompleteServiceOrigin = new Autocomplete(/** @type {import('../service/Autocomplete').AutocompleteOptions} */ ({
            autocompleteId: originAutocomplete.getAttribute('id'),
            label: i18n('routeFrom'),
            callbackFunction: this.handleAutocompleteRoutingSelect_.bind(this, MapRoutingMarkerType.ORIGIN),
        }));
        var autocompleteServiceDestination = new Autocomplete(/** @type {import('../service/Autocomplete').AutocompleteOptions} */ ({
            autocompleteId: destinationAutocomplete.getAttribute('id'),
            label: i18n('routeTo'),
            callbackFunction: this.handleAutocompleteRoutingSelect_.bind(this, MapRoutingMarkerType.DESTINATION),
        }));
        this.routingAutocompletes_ = {
            origin: autocompleteServiceOrigin,
            destination: autocompleteServiceDestination,
            steps: [],
        };
        this.listAutocompletes_.push(autocompleteServiceOrigin);
        this.listAutocompletes_.push(autocompleteServiceDestination);
    };
    /**
     * @private
     * @param {string} stepType Step type. `origin`, `destination` or `step`.
     * @param {import('../service/Autocomplete').GeEntitySuggest|LonLat} geOrLonLat GeEntity or Position `LonLat`
     */
    NavBar.prototype.handleAutocompleteRoutingSelect_ = function (stepType, geOrLonLat) {
        var position;
        var formattedAddress;
        if (geOrLonLat instanceof LonLat) {
            position = geOrLonLat;
            formattedAddress = "".concat(geOrLonLat.getLat(), ",").concat(geOrLonLat.getLon());
        }
        else {
            var ge = geOrLonLat;
            formattedAddress = ge.formatted_address;
            position = new LonLat(ge.coord.x, ge.coord.y);
        }
        this.mapRouting_.setRoutingStep(stepType, position, formattedAddress);
    };
    /**
     * @private
     * @param {import("../view/VWidgetRouting").SubStageDataMouseEvent} substageData Substage data
     */
    NavBar.prototype.handleMouseTapSubstage_ = function (substageData) {
        var _this = this;
        this.addSubstageFeatureOver_(substageData.wkt, true);
        this.substageClicked_ = true;
        if (IS_MOBILE) {
            this.routingWidget_.minimize();
        }
        // Secure 2000ms
        setTimeout(function () {
            _this.substageClicked_ = false;
        }, 2000);
    };
    /**
     * @private
     * @param {import("../view/VWidgetRouting").SubStageDataMouseEvent} substageData Substage data
     */
    NavBar.prototype.handleMouseEnterSubstage_ = function (substageData) {
        this.addSubstageFeatureOver_(substageData.wkt);
        this.addSubstageMarkerOver_(substageData.wkt, substageData.type, substageData.angle);
    };
    /**
     * @private
     * @param {string} wkt WKT.
     * @param {boolean} [zoom] Zoom.
     */
    NavBar.prototype.addSubstageFeatureOver_ = function (wkt, zoom) {
        if (zoom === void 0) { zoom = false; }
        if (this.substageFeature_ !== null) {
            this.cercaliaMap_.removeFeature(this.substageFeature_, this.featureLayer_);
        }
        var isLogistics = this.mapRouting_.isLogistics();
        // If is logistics, over feature gets default colors.
        this.substageFeature_ = new Feature({
            wkt: wkt,
            strokeColor: !isLogistics ? '#FF6E40' : undefined,
            strokeWidth: 8,
            strokeOpacity: 1,
            outlineColor: !isLogistics ? '#DD2C00' : undefined,
            outlineWidth: 10,
            outlineOpacity: 1,
            zIndex: 100,
        });
        this.cercaliaMap_.addFeature(this.substageFeature_, this.featureLayer_);
        if (zoom) {
            var bounds = this.substageFeature_.getBounds();
            var size = 18;
            this.cercaliaMap_.fitBounds(bounds, true, size);
        }
    };
    /**
     * @private
     * @param {string} wkt WKT.
     * @param {string} type Type.
     * @param {string} angle Angle.
     */
    NavBar.prototype.addSubstageMarkerOver_ = function (wkt, type, angle) {
        var geometry = wktToGeometry(wkt);
        var coord = geometry.coordinates[0];
        var position = new LonLat(coord[0], coord[1], Proj.EPSG4326);
        if (this.substageMarker_ !== null) {
            this.cercaliaMap_.removeMarker(this.substageMarker_, this.markerLayer_);
        }
        var filename = NavBar.getSignIcon(type, angle);
        this.substageMarker_ = new Marker(/** @type {import('../Marker').MarkerOptions} */ ({
            position: position,
            icon: new Icon(/** @type {import('../Icon').IconOptions} */ ({
                src: "".concat(IMG, "/route-signs/").concat(filename),
                anchor: [12, 12],
            })),
            zIndex: Infinity,
        }));
        this.cercaliaMap_.addMarker(this.substageMarker_, this.markerLayer_);
    };
    /**
     * @private
     */
    NavBar.prototype.handleMouseLeaveSubstage_ = function () {
        if (this.substageFeature_ !== null && !this.substageClicked_) {
            this.cercaliaMap_.removeFeature(this.substageFeature_, this.featureLayer_);
            this.substageFeature_ = null;
        }
        if (this.substageMarker_ !== null) {
            this.cercaliaMap_.removeMarker(this.substageMarker_, this.markerLayer_);
            this.substageMarker_ = null;
        }
    };
    /**
     * @private
     */
    NavBar.prototype.handleCloseReport_ = function () {
        if (this.substageFeature_ !== null) {
            this.cercaliaMap_.removeFeature(this.substageFeature_, this.featureLayer_);
        }
        this.mapRouting_.toggleFeaturesAlts(true);
    };
    /**
     * @private
     */
    NavBar.prototype.handleSaveRoute_ = function () {
        var wkt = this.mapRouting_.getWKT();
        if (wkt) {
            downloadKmlFromWkt(wkt);
        }
        else {
            var notification = new Notification(i18n('notificationErrorSavingKML'), Severity.ERROR);
            notification.display(this.cercaliaMap_);
        }
    };
    /**
     * @private
     * @param {string} type Type.
     * @param {string} angle Angle.
     * @return {string} Sign icon.
     */
    NavBar.getSignIcon = function (type, angle) {
        var angleN = parseInt(angle, 10);
        if (type === 'R') {
            return RoutingReportSigns.ROUNDABOUT;
        }
        if (type === 'G') {
            return RoutingReportSigns.FERRY;
        }
        if (type === 'H') {
            return RoutingReportSigns.FUNICULAR;
        }
        if (angleN > 314 && angleN < 350) {
            return RoutingReportSigns.KEEP_RIGHT;
        }
        if (angleN > 180 && angleN < 315) {
            return RoutingReportSigns.TURN_RIGHT;
        }
        if (angleN > 10 && angleN < 45) {
            return RoutingReportSigns.KEEP_LEFT;
        }
        if (angleN > 44 && angleN < 180) {
            return RoutingReportSigns.TURN_LEFT;
        }
        return RoutingReportSigns.RECTUM;
    };
    /**
     * @private
     */
    NavBar.prototype.initializePoisWidget_ = function () {
        var _this = this;
        getCategories(function (data) { return _this.poisWidget_.setCategories(data); }, currentDictionaryLanguage);
        this.mapPois_ = new PoisMap({
            map: this.getCercaliaMap(),
        });
        this.mapPois_.enable();
        // Autocomplete
        var autocompleteElement = this.poisWidget_.getAutocomplete();
        var autocompleteService = new Autocomplete(({
            autocompleteId: autocompleteElement.getAttribute('id'),
            callbackFunction: this.handleAutocompletePois_.bind(this),
        }));
        this.listAutocompletes_.push(autocompleteService);
        // Events
        this.poisWidget_.on(WidgetPoisEventType.CATEGORIES_CHANGED, function (categories) { return _this.handlePoisCategoriesChanged_(categories); });
        this.poisWidget_.on(WidgetPoisEventType.SEARCH_TYPE_CHANGED, function (typeSearch) {
            if (typeSearch === PoisSearchType.ADDRESS) {
                autocompleteService.enable();
            }
            else {
                autocompleteService.disable();
            }
        });
        this.poisWidget_.on(WidgetPoisEventType.POIS_SEARCH, function (searchOptions) { return _this.handlePoisSearch_(searchOptions); });
        this.poisWidget_.on(WidgetPoisEventType.CLOSE_POIS, function () { return _this.handleClosePois_(); });
    };
    /**
     * @private
     * @param {Object} elem Element
     * @return {Popup} Popup
     */
    NavBar.createPoiPopupDefault = function (elem) {
        return new Popup(/** @type {import('../Popup').PopupOptions} */ ({
            title: elem.name.value,
            content: "<div>".concat(elem.info.value, "</div>"),
            minWidth: 360,
            offset: [0, -20],
        }));
    };
    /**
     * @private
     * @param {Array.<string>} categories Categories
     */
    NavBar.prototype.handlePoisCategoriesChanged_ = function (categories) {
        this.cleanProximityMarkers_();
        this.mapPois_.setCategories(categories);
    };
    /**
     * @private
     * @param {import('../servers').GeEntity} ge GeEntity.
     */
    NavBar.prototype.handleAutocompletePois_ = function (ge) {
        var coord = ge.coord;
        var position = new LonLat(coord.x, coord.y);
        var zoom = (ge.type === 'st' || ge.type === 'adr' || ge.type === 'p') ? 15 : 12;
        this.cercaliaMap_.setCenter(position, zoom);
        this.poisWidget_.setGeoEntity(ge);
    };
    /**
     * @private
     * @param {import("../view/VWidgetPois").PoisSearchOptions} searchOptions Options
     */
    NavBar.prototype.handlePoisSearch_ = function (searchOptions) {
        if (searchOptions.categories.length > 0) {
            var position = void 0;
            if (searchOptions.searchType === PoisSearchType.ADDRESS) {
                if (searchOptions.ge) {
                    var coord = searchOptions.ge.coord;
                    position = new LonLat(coord.x, coord.y);
                }
                else {
                    var notification = new Notification(i18n('notificationPoisNotFound'), Severity.WARNING);
                    notification.display(this.cercaliaMap_);
                    return;
                }
            }
            else { // PoisSearchType.MAP_CENTER
                position = this.cercaliaMap_.getCenter();
            }
            this.mapPois_.setCategories([]);
            this.cleanProximityMarkers_();
            this.proximityService_.clear();
            this.proximityService_.setSearch(/** @type {import('../service/Proximity').ProximityOptions} */ ({
                x: position.getLon(),
                y: position.getLat(),
                srs: Proj.EPSG4326,
                rqpoicats: searchOptions.categories,
                num: searchOptions.numCand,
                rad: searchOptions.distance,
            }));
            this.proximityService_.proximity(this.handleProximityResult_.bind(this, position, searchOptions.distance));
        }
        else {
            var notification = new Notification(i18n('notificationSelectCategories'), Severity.WARNING);
            notification.display(this.cercaliaMap_);
        }
    };
    /**
     * @private
     * @param {LonLat} center Center position.
     * @param {number} radius Radius.
     * @param {import('../servers').ProximityResult} data Proximity result.
     */
    NavBar.prototype.handleProximityResult_ = function (center, radius, data) {
        var proximity = data.cercalia.proximity;
        var poilist = proximity.poilist;
        var pois = poilist && poilist.poi ? poilist.poi : [];
        for (var i = 0; i < pois.length; i += 1) {
            var poi = /** @type {import('../servers').PoiEntity} */ (pois[i]);
            var position = new LonLat(poi.coord.x, poi.coord.y);
            var name_1 = poi.name.value;
            var info = poi.info.value;
            var imgPoiName = poi.category_id;
            if (poi.subcategory_id && poi.subcategory_id !== '-1') {
                imgPoiName += "_".concat(poi.subcategory_id);
            }
            var marker = new Marker(/** @type {import('../Marker').MarkerOptions} */ ({
                position: position,
                icon: new Icon(/** @type {import('../Icon').IconOptions} */ ({
                    src: "".concat(IMG, "/pois/").concat(imgPoiName, ".png"),
                })),
                popup: new Popup(/** @type {import('../Popup').PopupOptions} */ ({
                    title: name_1,
                    content: info,
                    offset: [0, -20],
                })),
            }));
            this.markersProximity_.push(marker);
        }
        if (IS_MOBILE) {
            this.poisWidget_.minimize();
        }
        if (this.markersProximity_.length > 0) {
            this.drawProximityCircle_(center, radius);
            this.cercaliaMap_.addMarkers(this.markersProximity_, this.markerLayer_);
        }
        else {
            var notification = new Notification(i18n('notificationPoisNotFound'), Severity.WARNING);
            notification.display(this.cercaliaMap_);
        }
    };
    /**
     * @private
     * @param {LonLat} center Center.
     * @param {number} radius Radius.
     */
    NavBar.prototype.drawProximityCircle_ = function (center, radius) {
        this.markerProximityCenter_ = new Marker(/** @type {import('../Marker').MarkerOptions} */ ({
            position: center,
            simpleLabel: new SimpleLabel(/** @type {import('../SimpleLabel').SimpleLabelOptions} */ ({
                text: 'Center',
            })),
        }));
        this.circleProximity_ = new Feature({
            geometry: createCircleGeometry(center, radius),
            fillOpacity: 0.25,
            strokeOpacity: 1,
            strokeWidth: 2,
            outlineOpacity: 1,
            outlineWidth: 4,
        });
        this.cercaliaMap_.addMarker(this.markerProximityCenter_, this.markerLayer_);
        this.cercaliaMap_.addFeature(this.circleProximity_, this.featureLayer_);
        this.cercaliaMap_.centerToFeatures([this.circleProximity_]);
    };
    /**
     * @private
     */
    NavBar.prototype.cleanProximityMarkers_ = function () {
        if (this.markersProximity_.length > 0) {
            this.cercaliaMap_.removeMarkers(this.markersProximity_, this.markerLayer_);
            this.markersProximity_ = [];
        }
        if (this.markerProximityCenter_) {
            this.cercaliaMap_.removeMarker(this.markerProximityCenter_, this.markerLayer_);
            this.markerProximityCenter_ = null;
        }
        if (this.circleProximity_) {
            this.cercaliaMap_.removeFeature(this.circleProximity_, this.featureLayer_);
            this.circleProximity_ = null;
        }
    };
    /**
     * @private
     */
    NavBar.prototype.handleClosePois_ = function () {
        if (!isMobile) {
            this.cleanProximityMarkers_();
            this.poisWidget_.uncheckAll();
        }
    };
    /**
     * Reset menu to initial state.
     * @api
     */
    NavBar.prototype.reset = function () {
        this.clear();
        this.vNavBar_.reset();
    };
    /**
     * Clear all navbar autocomplete inputs.
     * @api
     */
    NavBar.prototype.clear = function () {
        this.listAutocompletes_.forEach(function (autocomplete) {
            autocomplete.setValue('');
        });
    };
    /**
     * @private
     */
    NavBar.prototype.updateGasolinePrices_ = function () {
        var _this = this;
        var poisService = new ServicePois(/** @type {import('../service/Pois').PoisOptions} */ ({
            pois: [this.GAS_STATION_POI_ID_],
        }));
        poisService.doPoisRequest(function (data) {
            if (!data.cercalia.error) {
                var poi = data.cercalia.map.poilist.poi;
                if (poi) {
                    var info = poi.info.value;
                    var arrInfo = info.split('|');
                    _this.gasolinePrices_ = /** @type {import("../view/VWidgetRouting").GasolinePrices} */ ({
                        gas95: arrInfo[8],
                        gas98: arrInfo[13],
                        diesel: arrInfo[9],
                        biodiesel: arrInfo[11],
                    });
                    _this.routingWidget_.updateGasolinePrices(_this.gasolinePrices_);
                    _this.mapRouting_.updateGasolinePrices(_this.gasolinePrices_);
                }
            }
        });
    };
    return NavBar;
}(Control));
export default NavBar;
//# sourceMappingURL=NavBar.js.map